import { useContext, useRef } from 'react'
import ReverTextField from '@/ui/components/portal-preview/pages/pages-components/rever-text-field'
import { usePortalTranslate } from '@/hooks'
import { useAppSelector } from '@/redux/hooks'
import { useSearchParams } from 'react-router'
import { PoweredByRever } from '@itsrever/design-system'
import { Button } from '@/ui/components/portal-preview/pages/pages-components/button'
import { EditedCopiesContext } from '@/ui/components/portal-preview/preview-contexts'
import {
  usePortalBackgroundImageUrl,
  usePortalBranding,
  usePortalLogoHeight,
  usePortalLogoUrl,
  usePreviewDevice
} from '@/ui/components/portal-preview/use-portal-branding'

export const VerticalForm = () => {
  const editedCopies = useContext(EditedCopiesContext)
  const { translate } = usePortalTranslate(editedCopies)

  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )
  // Via the hooks rather than the stored settings, so an editor's unsaved values preview.
  const backgroundUrl = usePortalBackgroundImageUrl()
  const logoUrl = usePortalLogoUrl()
  const { backgroundTextColor: textColor } = usePortalBranding()
  const logoHeight = usePortalLogoHeight()
  const eCommerceName = settings.name
  const returnsPolicyUrl = settings.external_return_policy_url

  // Eligibility handler
  const email = useRef<HTMLInputElement>(null)
  const orderNumber = useRef<HTMLInputElement>(null)
  const [searchParams] = useSearchParams()
  const preSelectedOrderNumber = searchParams.get('orderNumber')
  const preSelectedEmail = searchParams.get('email')
  const isDesktop = usePreviewDevice() === 'desktop'

  const content = (
    <>
        <div
          className="mb-6 flex w-full justify-center"
          style={{ height: logoHeight }}
        >
          <img className="h-min max-h-full max-w-full" src={logoUrl} />
        </div>
        <h4
          className="mt-2 text-center text-xl leading-7"
          style={{ color: textColor }}
          dangerouslySetInnerHTML={{
            __html: translate('landing_title_layout2')
          }}
        />
        <p
          className="mt-2 md:mb-4"
          style={{ color: textColor }}
          dangerouslySetInnerHTML={{
            __html: translate('landing_subtitle')
          }}
        />
        <div className="mt-6">
          <ReverTextField
            ref={orderNumber}
            defaultValue={preSelectedOrderNumber}
            label={translate('landing_numorder')}
            placeholder="X01234"
            fullWidth
            autoFocus
          />
          <div className="mt-4 w-full">
            <ReverTextField
              ref={email}
              defaultValue={preSelectedEmail}
              label={translate('landing_email')}
              placeholder={
                translate('landing_email_example') +
                '@' +
                settings.slug +
                '.com'
              }
              fullWidth
            />
          </div>
          <div className="mt-4">
            <Button>{translate('landing_button_continue')}</Button>
          </div>
          <div className="mt-4 flex flex-col items-center">
            {returnsPolicyUrl ? (
              <a
                className="text-center text-sm text-grey-2 underline"
                href={returnsPolicyUrl}
                target="_blank"
                rel="noreferrer"
                dangerouslySetInnerHTML={{
                  __html:
                    translate('landing_returns_policy') + (eCommerceName ?? '')
                }}
              />
            ) : null}
            <div className="mt-4 text-center text-xs text-grey-3">
              {translate('landing_user_politics1') + ' '}
              <a
                className="text-xs text-grey-3 underline"
                href="https://itsrever.com/user-terms/"
                target="_blank"
                rel="noreferrer"
                dangerouslySetInnerHTML={{
                  __html: translate('landing_user_politics2')
                }}
              />
              {' ' + translate('landing_user_politics3') + ' '}
              <a
                className="text-xs text-grey-3 underline"
                href="https://itsrever.com/user-terms/"
                target="_blank"
                rel="noreferrer"
                dangerouslySetInnerHTML={{
                  __html: translate('landing_user_politics4')
                }}
              />
            </div>
            <div className="mt-4">
              <div className="flex w-full items-center justify-center py-4">
                <PoweredByRever />
              </div>
            </div>
          </div>
        </div>
    </>
  )

  // The portal splits this layout in two from md: up — image on one half, the form panel filling
  // the other — rather than floating the card over the image the way it does on a phone.
  if (isDesktop) {
    return (
      <form className="grid h-full grid-cols-2" data-testid="verticalForm">
        <div
          className="size-full bg-grey-2 bg-cover bg-center"
          style={{ backgroundImage: `url(${backgroundUrl})` }}
        />
        <div className="flex items-center justify-center overflow-y-auto bg-white p-8">
          <div className="w-full max-w-[26rem]">{content}</div>
        </div>
      </form>
    )
  }

  return (
    <form
      className="flex h-full flex-col place-content-center items-center rounded-[35px]"
      data-testid="verticalForm"
      style={{
        backgroundImage: `url(${backgroundUrl})`,
        backgroundSize: 'cover'
      }}
    >
      <div></div>
      <div className="m-10 rounded-lg bg-white p-4 shadow-md shadow-black/25">
        {content}
      </div>
    </form>
  )
}

export default VerticalForm
